import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Form, Button, Tabs, Tab, Card, Badge } from 'react-bootstrap';
import ReportEditor from '../../components/ReportEditor';
import { generateForensicReport } from '../../templates/forensicReportTemplate';
import { Case, Analysis, CreateAnalysisRequest, RequiredFile, CreateRequiredFileRequest, UpdateRequiredFileRequest } from '../../types';
import { fetchCaseById, saveCase, fetchCaseAnalyses, createAnalysis, deleteAnalysis, fetchRequiredFiles, createRequiredFile, updateRequiredFile, deleteRequiredFile, manuallyUnmatchFile, fetchCaseFiles, syncCourtDocket, generateCaseOverview, createCaseActivityLogEntry } from '../../api/cases';
import FileUploadZone from '../../components/FileUploadZone';
import AuditLogTable from './AuditLogTable';
import CaseActivityLog from './CaseActivityLog';
import CaseBilling from './CaseBilling';
import { CASE_TYPE_OPTIONS, CASE_STAGE_OPTIONS, WAITING_ON_OPTIONS } from '../../constants/constants';
import CaseFiles from './CaseFiles';
import { useAuth } from '../../contexts/AuthContext';
import ListAnalyses from './ListAnalyses';
import CreateAnalysisModal from './CreateAnalysisModal';
import CaseUsers from './CaseUsers';
import DocumentsTable from './DocumentsTable';
import CaseStats from './CaseStats';
import RequiredFilesList from './RequiredFilesList';
import DataExport from './DataExport';
import CreateRequiredFileModal from './CreateRequiredFileModal';
import EditRequiredFileModal from './EditRequiredFileModal';
import CaseEntities from './CaseEntities';
import CaseAgent from './CaseAgent';
import CaseChat from './CaseChat';
import EntityGraph from './EntityGraph';
import { trackEvent } from '../../utils/analytics';

const ViewCase: React.FC = () => {
  const { case_id, run_id } = useParams<{ case_id: string; run_id?: string }>();
  const [caseDetails, setCaseDetails] = useState<Case | null>(null);
  const [editedCase, setEditedCase] = useState<Case | null>(null);
  const [activeTab, setActiveTab] = useState(run_id ? 'agent' : 'details');
  const [isEditing, setIsEditing] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [activityExpanded, setActivityExpanded] = useState(true);
  const [syncingCourt, setSyncingCourt] = useState(false);
  const [courtSyncMessage, setCourtSyncMessage] = useState<string | null>(null);
  const [courtSyncError, setCourtSyncError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [activityRefresh, setActivityRefresh] = useState(0);
  const [billingExpanded, setBillingExpanded] = useState(true);
  const [waitingEditing, setWaitingEditing] = useState(false);
  const [waitingDraft, setWaitingDraft] = useState({ waiting_on: '', waiting_on_note: '' });
  const [savingWaiting, setSavingWaiting] = useState(false);
  const [overviewExpanded, setOverviewExpanded] = useState(true);
  const [regeneratingOverview, setRegeneratingOverview] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [auditRefreshTrigger, setAuditRefreshTrigger] = useState(0);
  const [showCreateAnalysis, setShowCreateAnalysis] = useState(false);
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [showCreateRequirement, setShowCreateRequirement] = useState(false);
  const [showEditRequirement, setShowEditRequirement] = useState(false);
  const [requirementToEdit, setRequirementToEdit] = useState<RequiredFile | null>(null);
  const [requirements, setRequirements] = useState<RequiredFile[]>([]);
  const [caseFiles, setCaseFiles] = useState<any[]>([]);
  const [reportHtml, setReportHtml] = useState<string>('');

  const filesComponentRef = useRef<{ loadFiles: () => void }>(null);
  const auth = useAuth();

  useEffect(() => {
    if (case_id) {
      trackEvent('case_viewed', { case_id });
    }
  }, [case_id]);

  const refreshAnalyses = useCallback(async () => {
    try {
      const response = await fetchCaseAnalyses(case_id!, auth.user?.id_token);
      setAnalyses(response.data.analyses);
    } catch (err) {
      console.error('Error loading analyses:', err);
    }
  }, [case_id, auth.user?.id_token]);

  const refreshRequirements = useCallback(async () => {
    try {
      const response = await fetchRequiredFiles(case_id!, auth.user?.id_token);
      setRequirements(response.data.requirements || []);
    } catch (err) {
      console.error('Error loading requirements:', err);
    }
  }, [case_id, auth.user?.id_token]);

  useEffect(() => {
    const fetchCaseDetails = async () => {
      try {
        const response = await fetchCaseById(case_id!, auth.user?.id_token);
        const data = await response.data;
        setCaseDetails(data);
        setEditedCase(data);
      } catch (err) {
        console.error('Error fetching case:', err);
      }
    };

    if (auth.user?.id_token) {
      fetchCaseDetails();
    }
  }, [case_id, auth.user?.id_token]);

  useEffect(() => {
    const hasFieldChanges = JSON.stringify(caseDetails) !== JSON.stringify(editedCase);
    setHasChanges(hasFieldChanges);
  }, [editedCase, caseDetails]);

  useEffect(() => {
    const loadAnalyses = async () => {
      try {
        const response = await fetchCaseAnalyses(case_id!, auth.user?.id_token);
        setAnalyses(response.data.analyses);
      } catch (err) {
        console.error('Error loading analyses:', err);
      }
    };

    if (auth.user?.id_token) {
      loadAnalyses();
    }
  }, [case_id, auth.user?.id_token]);

  useEffect(() => {
    if (auth.user?.id_token) {
      refreshRequirements();
    }
  }, [case_id, auth.user?.id_token, refreshRequirements]);

  const handleInputChange = (
    field: keyof Case,
    value: string | number
  ) => {
    setEditedCase((prev) => ({
      ...prev!,
      [field]: value,
    }));
  };

  const saveChanges = async (): Promise<boolean> => {
    try {
      setIsSaving(true);
      await saveCase(
        editedCase!,
        case_id!,
        [],
        auth.user?.id_token,
        () => {},
        () => {}
      );
      setCaseDetails(editedCase);
      setAuditRefreshTrigger(prev => prev + 1);
      return true;
    } catch (error: any) {
      console.error('Error saving case:', error);
      if (error.response?.status === 403) {
        alert('Access denied. Please check your permissions.');
      } else if (error.response?.data?.error) {
        alert(`Save failed: ${error.response.data.error}`);
      } else {
        alert('Failed to save case. Please try again.');
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const startWaitingEdit = () => {
    setWaitingDraft({
      waiting_on: caseDetails?.waiting_on || '',
      waiting_on_note: caseDetails?.waiting_on_note || '',
    });
    setWaitingEditing(true);
  };

  // Saves just the waiting-on fields (saveCase replaces the whole record, so
  // send the current case with them merged in) and records the change in the
  // activity log so the case's history shows how long it sat blocked.
  const saveWaitingOn = async () => {
    if (!caseDetails || savingWaiting) return;
    const waiting_on = waitingDraft.waiting_on;
    const waiting_on_note = waiting_on ? waitingDraft.waiting_on_note.trim() : '';
    const prev = caseDetails.waiting_on || '';
    if (waiting_on === prev && waiting_on_note === (caseDetails.waiting_on_note || '')) {
      setWaitingEditing(false);
      return;
    }
    const updated: Case = {
      ...caseDetails,
      waiting_on,
      waiting_on_note,
      waiting_since: !waiting_on ? '' : waiting_on !== prev ? new Date().toISOString().slice(0, 10) : caseDetails.waiting_since,
    };
    setSavingWaiting(true);
    try {
      await saveCase(updated, case_id!, [], auth.user?.id_token, () => {}, () => {});
      setCaseDetails(updated);
      setEditedCase(updated);
      setAuditRefreshTrigger(prev => prev + 1);
      setWaitingEditing(false);
      const note = waiting_on
        ? `Status: waiting on ${waiting_on}${waiting_on_note ? ` - ${waiting_on_note}` : ''}`
        : `Status: no longer waiting${prev ? ` on ${prev}` : ''}`;
      try {
        await createCaseActivityLogEntry(case_id!, note, auth.user?.id_token);
        setActivityRefresh(prev => prev + 1);
      } catch (err) {
        console.error('Waiting-on saved, but logging it to the activity log failed:', err);
      }
    } catch (error: any) {
      alert(`Failed to update waiting-on status: ${error.response?.data?.error || error.message}`);
    } finally {
      setSavingWaiting(false);
    }
  };

  const waitingDays = caseDetails?.waiting_on && caseDetails.waiting_since
    ? Math.max(0, Math.floor((Date.now() - new Date(`${caseDetails.waiting_since}T00:00:00`).getTime()) / 86_400_000))
    : null;

  const handleCreateAnalysis = async (analysisData: any) => {
    // Deliberately not caught here - CreateAnalysisModal awaits this and shows
    // the error inline (keeping the modal open) rather than it silently
    // failing with the modal just closing and nothing visibly happening.
    const request: CreateAnalysisRequest = {
      case_id: case_id!,
      type: analysisData.type,
      name: analysisData.name,
      description: analysisData.description,
      inputs: analysisData.inputs
    };

    await createAnalysis(request, auth.user?.id_token);
    trackEvent('analysis_run', { case_id: case_id!, analysis_type: analysisData.type as string });
    // Refresh analyses list
    const response = await fetchCaseAnalyses(case_id!, auth.user?.id_token);
    setAnalyses(response.data.analyses);
  };

  const handleDeleteAnalysis = async (analysis_id: string) => {
    try {
      await deleteAnalysis(case_id!, analysis_id, auth.user?.id_token);
      // Refresh analyses list
      const response = await fetchCaseAnalyses(case_id!, auth.user?.id_token);
      setAnalyses(response.data.analyses);
    } catch (err) {
      console.error('Error deleting analysis:', err);
    }
  };

  const handleCreateRequirement = async (requirementData: CreateRequiredFileRequest) => {
    try {
      await createRequiredFile(requirementData, auth.user?.id_token);
      await refreshRequirements();
      setAuditRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error creating requirement:', err);
      alert('Failed to create requirement. Please try again.');
    }
  };

  const handleSyncCourtDocket = async () => {
    if (!case_id || syncingCourt) return;
    setSyncingCourt(true);
    setCourtSyncMessage(null);
    setCourtSyncError(null);
    try {
      const result = await syncCourtDocket(case_id, auth.user?.id_token);
      setCourtSyncMessage(
        result.new_documents === 0 && result.new_events === 0
          ? 'Up to date - no new documents or docket events.'
          : `Added ${result.new_documents} new document${result.new_documents === 1 ? '' : 's'} and ${result.new_events} new docket event${result.new_events === 1 ? '' : 's'}.`
      );
      setRefreshCounter(prev => prev + 1);
      setAuditRefreshTrigger(prev => prev + 1);
    } catch (err: any) {
      setCourtSyncError(err.response?.data?.error || err.message || 'Failed to sync court docket');
    } finally {
      setSyncingCourt(false);
    }
  };

  const handleRegenerateOverview = async () => {
    if (!case_id || regeneratingOverview) return;
    setRegeneratingOverview(true);
    setOverviewError(null);
    try {
      const result = await generateCaseOverview(case_id, auth.user?.id_token);
      setCaseDetails(prev => prev ? { ...prev, case_overview: result.case_overview, case_overview_updated_at: new Date().toISOString() } : prev);
    } catch (err: any) {
      setOverviewError(err.response?.data?.error || err.message || 'Failed to generate case overview');
    } finally {
      setRegeneratingOverview(false);
    }
  };

  const handleEditRequirement = (requirement: RequiredFile) => {
    setRequirementToEdit(requirement);
    setShowEditRequirement(true);
  };

  const handleUpdateRequirement = async (requirement_id: string, updateData: UpdateRequiredFileRequest) => {
    try {
      await updateRequiredFile(case_id!, requirement_id, updateData, auth.user?.id_token);
      await refreshRequirements();
      setAuditRefreshTrigger(prev => prev + 1);
      setShowEditRequirement(false);
      setRequirementToEdit(null);
    } catch (err) {
      console.error('Error updating requirement:', err);
      alert('Failed to update requirement. Please try again.');
    }
  };

  const handleDeleteRequirement = async (requirement_id: string) => {
    try {
      await deleteRequiredFile(case_id!, requirement_id, auth.user?.id_token);
      await refreshRequirements();
      setAuditRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error deleting requirement:', err);
      alert('Failed to delete requirement. Please try again.');
    }
  };

  const refreshCaseFiles = useCallback(async () => {
    try {
      const response = await fetchCaseFiles(case_id!, auth.user?.id_token);
      const activeFiles = response.data.files.filter((file: any) => !file.archived);
      setCaseFiles(activeFiles);
    } catch (err) {
      console.error('Error loading case files:', err);
    }
  }, [case_id, auth.user?.id_token]);

  const handleUnmatchFile = async (requirement_id: string, file_id: string, month?: string) => {
    try {
      await manuallyUnmatchFile(case_id!, file_id, requirement_id, month, auth.user?.id_token);
      await refreshRequirements();
      await refreshCaseFiles();
      setAuditRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error unmatching file:', err);
      alert('Failed to unmatch file. Please try again.');
      throw err;
    }
  };

  useEffect(() => {
    if (auth.user?.id_token && case_id) {
      refreshCaseFiles();
    }
  }, [auth.user?.id_token, case_id, refreshCaseFiles]);

  useEffect(() => {
    // load saved draft from localStorage if present
    try {
      const key = `case_report_${case_id}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        setReportHtml(saved);
      }
    } catch (err) {
      // ignore localStorage errors
    }
  }, [case_id]);

  if (!caseDetails || !editedCase) {
    return <p>Loading case details...</p>;
  }

  return (
    <div>
      <div className="mb-2">
        <Link to="/cases" className="text-secondary text-decoration-none">
          ← Back to Cases
        </Link>
      </div>
      <div className="mb-3">
        <h2>{caseDetails.case_title}</h2>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'details')}>
        <Tab eventKey="details" title="Case Details">
          {!isEditing ? (
            <div className="mb-3 mt-2">
              <div className="d-flex justify-content-between align-items-start">
                <div className="d-flex gap-4 flex-wrap">
                  {([
                    ['Case No.', caseDetails.case_number],
                    ['Client', caseDetails.client],
                    ['Type', caseDetails.case_type],
                    ['Start Date', caseDetails.start_date],
                  ] as [string, string | undefined][]).map(([label, value]) => (
                    <div key={label}>
                      <div style={{ fontSize: '0.68rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 500, marginBottom: 2 }}>
                        {label}
                      </div>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem', color: '#111827' }}>
                        {value || '—'}
                      </div>
                    </div>
                  ))}
                  <div>
                    <div style={{ fontSize: '0.68rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 500, marginBottom: 2 }}>
                      Waiting On
                    </div>
                    {waitingEditing ? (
                      <div className="d-flex gap-1 align-items-center flex-wrap">
                        <Form.Select
                          size="sm"
                          style={{ maxWidth: 180 }}
                          value={waitingDraft.waiting_on}
                          onChange={(e) => setWaitingDraft({ ...waitingDraft, waiting_on: e.target.value })}
                        >
                          <option value="">Nothing - not blocked</option>
                          {WAITING_ON_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </Form.Select>
                        {waitingDraft.waiting_on && (
                          <Form.Control
                            size="sm"
                            style={{ maxWidth: 240 }}
                            placeholder="Details (e.g. 2022 statements from Chase)"
                            value={waitingDraft.waiting_on_note}
                            onChange={(e) => setWaitingDraft({ ...waitingDraft, waiting_on_note: e.target.value })}
                          />
                        )}
                        <Button size="sm" variant="primary" onClick={saveWaitingOn} disabled={savingWaiting}>
                          {savingWaiting ? '...' : 'Save'}
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setWaitingEditing(false)} disabled={savingWaiting}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none text-start"
                        title="Change what this case is waiting on"
                        onClick={startWaitingEdit}
                      >
                        {caseDetails.waiting_on ? (
                          <span>
                            <Badge bg="warning" text="dark">{caseDetails.waiting_on}</Badge>
                            {waitingDays !== null && (
                              <span className="small text-muted ms-1">{waitingDays === 0 ? 'since today' : `${waitingDays}d`}</span>
                            )}
                            {caseDetails.waiting_on_note && (
                              <span className="small text-muted ms-1">· {caseDetails.waiting_on_note}</span>
                            )}
                          </span>
                        ) : (
                          <span className="small text-muted">Not blocked - set</span>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="link"
                    size="sm"
                    className="text-muted p-0"
                    onClick={() => setDetailsExpanded(!detailsExpanded)}
                    title={detailsExpanded ? 'Show less' : 'Show more'}
                  >
                    {detailsExpanded ? '▾' : '▸'}
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => setIsEditing(true)}>
                    Edit
                  </Button>
                </div>
              </div>

              {detailsExpanded && (
                <Form className="mt-3">
                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Case Number</Form.Label>
                        <Form.Control type="text" value={caseDetails.case_number} disabled size="sm" />
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Case ID</Form.Label>
                        <Form.Control type="text" value={caseDetails.case_id} disabled size="sm" />
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Start Date</Form.Label>
                        <Form.Control type="date" value={caseDetails.start_date || ''} disabled size="sm" />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Title</Form.Label>
                        <Form.Control type="text" value={caseDetails.case_title} disabled size="sm" />
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Client</Form.Label>
                        <Form.Control type="text" value={caseDetails.client || ''} disabled size="sm" />
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Type</Form.Label>
                        <Form.Control type="text" value={caseDetails.case_type || ''} disabled size="sm" />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Docket Number</Form.Label>
                        <Form.Control type="text" value={caseDetails.docket_number || ''} disabled size="sm" />
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Opposing Party</Form.Label>
                        <Form.Control type="text" value={caseDetails.opposing_party || ''} disabled size="sm" />
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Deadline</Form.Label>
                        <Form.Control type="date" value={caseDetails.deadline || ''} disabled size="sm" />
                      </Form.Group>
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Contact Name</Form.Label>
                        <Form.Control type="text" value={caseDetails.contact_name || ''} disabled size="sm" />
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Contact Phone</Form.Label>
                        <Form.Control type="text" value={caseDetails.contact_phone || ''} disabled size="sm" />
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group className="mb-2">
                        <Form.Label className="small">Stage</Form.Label>
                        <Form.Control type="text" value={caseDetails.case_stage || ''} disabled size="sm" />
                      </Form.Group>
                    </div>
                  </div>
                </Form>
              )}
            </div>
          ) : (
            <div>
              <Form>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Case Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={caseDetails.case_number}
                        disabled
                        size="sm"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Case ID</Form.Label>
                      <Form.Control type="text" value={caseDetails.case_id} disabled size="sm" />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={editedCase.start_date || ''}
                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                        size="sm"
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Title</Form.Label>
                      <Form.Control
                        type="text"
                        value={editedCase.case_title}
                        onChange={(e) => handleInputChange('case_title', e.target.value)}
                        size="sm"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Client</Form.Label>
                      <Form.Control
                        type="text"
                        value={editedCase.client || ''}
                        onChange={(e) => handleInputChange('client', e.target.value)}
                        size="sm"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Type</Form.Label>
                      <Form.Select
                        value={editedCase.case_type}
                        onChange={(e) => handleInputChange('case_type', e.target.value)}
                        size="sm"
                      >
                        <option value="">Select Case Type</option>
                        {CASE_TYPE_OPTIONS.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Docket Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={editedCase.docket_number || ''}
                        onChange={(e) => handleInputChange('docket_number', e.target.value)}
                        size="sm"
                        placeholder="e.g. 45D01-2112-PL-000885"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Opposing Party</Form.Label>
                      <Form.Control
                        type="text"
                        value={editedCase.opposing_party || ''}
                        onChange={(e) => handleInputChange('opposing_party', e.target.value)}
                        size="sm"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Deadline</Form.Label>
                      <Form.Control
                        type="date"
                        value={editedCase.deadline || ''}
                        onChange={(e) => handleInputChange('deadline', e.target.value)}
                        size="sm"
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Contact Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={editedCase.contact_name || ''}
                        onChange={(e) => handleInputChange('contact_name', e.target.value)}
                        size="sm"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Contact Phone</Form.Label>
                      <Form.Control
                        type="text"
                        value={editedCase.contact_phone || ''}
                        onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                        size="sm"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Stage</Form.Label>
                      <Form.Select
                        value={editedCase.case_stage || ''}
                        onChange={(e) => handleInputChange('case_stage', e.target.value)}
                        size="sm"
                      >
                        <option value="">Not started</option>
                        {CASE_STAGE_OPTIONS.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>
              </Form>
              <div className="d-flex gap-2 mt-2 mb-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => { setEditedCase(caseDetails); setIsEditing(false); }}
                >
                  Cancel
                </Button>
                {hasChanges && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={async () => { const ok = await saveChanges(); if (ok) setIsEditing(false); }}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                )}
              </div>
            </div>
          )}

          <Card className="mb-3">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setOverviewExpanded(!overviewExpanded)}
            >
              <span className="fw-semibold">Case Overview (AI-generated)</span>
              <span className="text-muted">{overviewExpanded ? '▾' : '▸'}</span>
            </Card.Header>
            {overviewExpanded && (
              <Card.Body>
                {caseDetails.case_overview ? (
                  <>
                    {caseDetails.case_overview_updated_at && (
                      <div className="text-muted small mb-2">
                        Last updated {new Date(caseDetails.case_overview_updated_at).toLocaleString()}
                      </div>
                    )}
                    <div style={{ whiteSpace: 'pre-wrap' }}>{caseDetails.case_overview}</div>
                  </>
                ) : (
                  <div className="text-muted small mb-2">
                    No overview generated yet. It's auto-generated after each court sync that finds new material, or you can generate one now.
                  </div>
                )}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mt-2"
                  onClick={handleRegenerateOverview}
                  disabled={regeneratingOverview}
                >
                  {regeneratingOverview ? 'Generating...' : caseDetails.case_overview ? 'Regenerate' : 'Generate Overview'}
                </Button>
                {overviewError && <div className="text-danger small mt-1">{overviewError}</div>}
              </Card.Body>
            )}
          </Card>

          <Card className="mb-3">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setActivityExpanded(!activityExpanded)}
            >
              <span className="fw-semibold">Case Notes / Activity Log</span>
              <span className="text-muted">{activityExpanded ? '▾' : '▸'}</span>
            </Card.Header>
            {activityExpanded && (
              <Card.Body>
                <CaseActivityLog key={`${refreshCounter}-${activityRefresh}`} />
              </Card.Body>
            )}
          </Card>

          <Card className="mb-3">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setBillingExpanded(!billingExpanded)}
            >
              <span className="fw-semibold">Time &amp; Payments</span>
              <span className="text-muted">{billingExpanded ? '▾' : '▸'}</span>
            </Card.Header>
            {billingExpanded && (
              <Card.Body>
                <CaseBilling />
              </Card.Body>
            )}
          </Card>

          <CaseStats case_id={case_id!} />

          {caseDetails.docket_number && (
            <div className="mb-2">
              <div className="d-flex align-items-center gap-2">
                <Button variant="outline-secondary" size="sm" onClick={handleSyncCourtDocket} disabled={syncingCourt}>
                  {syncingCourt ? 'Syncing court records...' : 'Sync Court Records'}
                </Button>
                {caseDetails.last_court_sync && !syncingCourt && (
                  <span className="text-muted small">
                    Last synced {new Date(caseDetails.last_court_sync).toLocaleString()}
                  </span>
                )}
              </div>
              {courtSyncMessage && <div className="text-success small mt-1">{courtSyncMessage}</div>}
              {courtSyncError && <div className="text-danger small mt-1">{courtSyncError}</div>}
            </div>
          )}

          <DocumentsTable key={refreshCounter} caseId={case_id!} />
        </Tab>
        <Tab eventKey="files" title="Files">
          <div className="mb-4">
            <FileUploadZone
              case_id={case_id!}
              id_token={auth.user?.id_token}
              onUploadComplete={() => {
                if (filesComponentRef.current) {
                  filesComponentRef.current.loadFiles();
                }
                setAuditRefreshTrigger(prev => prev + 1);
              }}
            />
          </div>
          <CaseFiles 
            case_id={case_id!} 
            ref={filesComponentRef} 
            onFileDeleted={() => setAuditRefreshTrigger(prev => prev + 1)}
            requirements={requirements}
            onFileMatched={async () => {
              await refreshRequirements();
              await refreshCaseFiles();
              setAuditRefreshTrigger(prev => prev + 1);
            }}
          />
        </Tab>
        <Tab eventKey="required-files" title="Required Files">
          <RequiredFilesList
            case_id={case_id!}
            requirements={requirements}
            onCreateRequirement={() => setShowCreateRequirement(true)}
            onEditRequirement={handleEditRequirement}
            onDeleteRequirement={handleDeleteRequirement}
            onRefresh={refreshRequirements}
            onUnmatchFile={handleUnmatchFile}
            caseFiles={caseFiles}
          />
        </Tab>
        <Tab eventKey="analyses" title="Tools">
          <ListAnalyses 
            analyses={analyses}
            onCreateAnalysis={() => setShowCreateAnalysis(true)}
            onDeleteAnalysis={handleDeleteAnalysis}
            onRefresh={refreshAnalyses}
          />
        </Tab>
        <Tab eventKey="agent" title="AI Analysis">
          <CaseAgent case_id={case_id!} case_type={caseDetails?.case_type} initialRunId={run_id} />
        </Tab>
        <Tab eventKey="chat" title="Chat">
          <CaseChat case_id={case_id!} />
        </Tab>
        <Tab eventKey="entities" title="Entities">
          <CaseEntities case_id={case_id!} />
        </Tab>
        <Tab eventKey="money-flow" title="Money Flow">
          <div className="mt-3">
            <EntityGraph case_id={case_id!} />
          </div>
        </Tab>
        <Tab eventKey="data-export" title="Data Export">
          <DataExport case_id={case_id!} />
        </Tab>
        <Tab eventKey="users" title="Users">
          <CaseUsers case_id={case_id!} />
        </Tab>
        <Tab eventKey="audit" title="Audit Log">
          <div className="mt-3">
            <h3>Audit History</h3>
            <AuditLogTable refreshTrigger={auditRefreshTrigger} />
          </div>
        </Tab>
        {/* Hidden until reports are ready */}
        {false && <Tab eventKey="report" title="Report">
          <div className="mb-3 d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const html = generateForensicReport(caseDetails, caseFiles, analyses);
                setReportHtml(html);
              }}
            >
              Generate Draft
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                // clear draft
                setReportHtml('');
                try { localStorage.removeItem(`case_report_${case_id}`); } catch (e) {}
              }}
            >
              Clear Draft
            </Button>
            <div style={{ flex: 1 }} />
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!reportHtml}
              onClick={() => {
                // open printable window for browser PDF export
                try {
                  const printWindow = window.open('', '_blank');
                  if (!printWindow) {
                    alert('Popup blocked. Please allow popups to export PDF.');
                    return;
                  }
                  const doc = printWindow.document;
                  doc.open();
                  doc.write(`
                    <html>
                      <head>
                        <title>Forensic Report - ${caseDetails.case_title}</title>
                        <meta charset="utf-8" />
                        <style>body{font-family: 'Times New Roman', serif; padding:20px;}</style>
                      </head>
                      <body>${reportHtml}</body>
                    </html>
                  `);
                  doc.close();
                  printWindow.focus();
                  // Give the new window a moment to render then call print
                  setTimeout(() => {
                    printWindow.print();
                  }, 300);
                } catch (e) {
                  alert('Failed to open print window.');
                }
              }}
            >
              Export PDF (Print)
            </Button>
          </div>

          <ReportEditor
            initialHtml={reportHtml}
            onSave={(html) => {
              setReportHtml(html);
              try {
                localStorage.setItem(`case_report_${case_id}`, html || '');
                alert('Draft saved locally (demo).');
              } catch (e) {
                alert('Failed to save draft locally.');
              }
            }}
          />
        </Tab>}
      </Tabs>

      <CreateAnalysisModal
        show={showCreateAnalysis}
        onHide={() => setShowCreateAnalysis(false)}
        onSubmit={handleCreateAnalysis}
        case_id={case_id!}
      />

      <CreateRequiredFileModal
        show={showCreateRequirement}
        onHide={() => setShowCreateRequirement(false)}
        onSubmit={handleCreateRequirement}
        case_id={case_id!}
      />
      <EditRequiredFileModal
        show={showEditRequirement}
        onHide={() => {
          setShowEditRequirement(false);
          setRequirementToEdit(null);
        }}
        onSubmit={handleUpdateRequirement}
        requirement={requirementToEdit}
      />
    </div>
  );
};

export default ViewCase;
